import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

// Type for failure records in plot data (different from API FailureRecord)
type PlotFailureRecord = {
  case_id: string;
  turbine_id: string;
  site_name: string;
  component_major: string;
  component_minor: string;
  failure_mode: string;
  outcome_name: string;
  failure_date: string;
  operation_time_years: number;
  turbine_model: string;
  turbine_name: string;
};

// Define tooltip parameter types
interface TooltipParams {
  componentType: string;
  seriesName: string;
  value?: [number, number];
  data?: {
    record?: {
      case_id: string;
      turbine_id: string;
      site_name: string;
      component_major: string;
      component_minor?: string;
      failure_mode?: string;
      outcome_name: string;
      failure_date: string;
    };
    originalTime?: number;
    originalProbability?: number;
    count?: number;
    symbolSize?: number;
  };
}

// Weibull probability transformation functions
const weibullY = (probability: number): number => {
  return Math.log(-Math.log(1 - probability));
};

const inverseWeibullY = (y: number): number => {
  return 1 - Math.exp(-Math.exp(y));
};

// Color palettes for different categories
const COLOR_PALETTES = {
  default: ['#3b82f6'],
  category10: [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
  ],
  category20: [
    '#3b82f6', '#93c5fd', '#ef4444', '#fca5a5', '#10b981', '#86efac',
    '#f59e0b', '#fde047', '#8b5cf6', '#c4b5fd', '#ec4899', '#f9a8d4',
    '#14b8a6', '#5eead4', '#f97316', '#fdba74', '#06b6d4', '#67e8f9',
    '#84cc16', '#bef264'
  ],
  timePeriod: {
    gradient: ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'],
  }
};

// Marker shapes for different categories
const MARKER_SHAPES = ['circle', 'rect', 'triangle', 'diamond', 'pin'];

// Helper function to get size legend data based on size option
const getSizeLegendData = (sizeBy: SizeByOption) => {
  if (sizeBy === 'operationTime') {
    return [
      { name: 'Long Operation', icon: 'circle' },
      { name: 'Medium Operation', icon: 'circle' },
      { name: 'Short Operation', icon: 'circle' }
    ];
  } else if (sizeBy === 'recent') {
    return [
      { name: 'Recent (< 30 days)', icon: 'circle' },
      { name: '30-90 days', icon: 'circle' },
      { name: '90-365 days', icon: 'circle' },
      { name: 'Older (> 1 year)', icon: 'circle' }
    ];
  }
  return [];
};

interface UseWeibullChartOptionsProps {
  analysis: WeibullAnalysisResult;
  showConfidenceIntervals: boolean;
  confidenceLevel: number;
  showCensoredData: boolean;
  censoredData?: CensoredDataPoint[];
  chartType: 'probability' | 'cdf';
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

export const useWeibullChartOptions = ({
  analysis,
  showConfidenceIntervals,
  confidenceLevel: _confidenceLevel,
  showCensoredData,
  censoredData,
  chartType,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}: UseWeibullChartOptionsProps) => {
  return useMemo<EChartsOption>(() => {
    const operationTimes = analysis.plotData.operationTimes;
    const censoredTimes = showCensoredData && censoredData ? censoredData.map((d) => d.time) : [];
    const allTimes = [...operationTimes, ...censoredTimes].filter((t) => t > 0);

    const dataMin = allTimes.length > 0 ? Math.min(...allTimes) : 0.1;
    const dataMax = allTimes.length > 0 ? Math.max(...allTimes) : 10;

    const span = dataMax - dataMin;
    let interval: number;
    if (span <= 5) {
      interval = 1;
    } else if (span <= 10) {
      interval = 2;
    } else if (span <= 14) {
      interval = 2;
    } else {
      interval = 5;
    }

    const minBoundary = Math.floor(dataMin / interval) * interval;
    const maxBoundary = Math.ceil(dataMax / interval) * interval;

    const getNiceLogMin = (value: number): number => {
      const niceValues = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100];
      for (let i = niceValues.length - 1; i >= 0; i--) {
        if (niceValues[i] <= value) {
          return niceValues[i];
        }
      }
      return 0.1;
    };

    const logMinBoundary = getNiceLogMin(dataMin);

    // Helper function to get category value for a record
    const getCategoryValue = (record: PlotFailureRecord, category: ColorByOption | MarkerByOption): string => {
      if (!record) return 'Unknown';
      switch (category) {
        case 'site':
          return record.site_name || 'Unknown Site';
        case 'component':
          return record.component_major || 'Unknown Component';
        case 'subComponent':
          return record.component_minor || 'Unknown Sub-Component';
        case 'turbine':
          return record.turbine_model || 'Unknown Model';
        case 'outcome':
          return record.outcome_name || 'Unknown Outcome';
        case 'timePeriod': {
          const year = new Date(record.failure_date).getFullYear();
          if (year < 2020) return 'Before 2020';
          if (year < 2022) return '2020-2021';
          if (year < 2024) return '2022-2023';
          return '2024+';
        }
        default:
          return 'Default';
      }
    };

    // Get unique categories for color and marker mapping
    const getUniqueCategories = (option: ColorByOption | MarkerByOption): string[] => {
      if (option === 'none') return ['Default'];
      const categories = new Set<string>();
      analysis.plotData.failureRecords?.forEach(record => {
        categories.add(getCategoryValue(record, option));
      });
      const sorted = Array.from(categories).sort();
      if (sorted.length > 20) {
        return [...sorted.slice(0, 20), 'Other'];
      }
      return sorted;
    };

    // Create marker mapping
    const getMarker = (record: PlotFailureRecord): string => {
      if (markerBy === 'none') return 'circle';
      const category = getCategoryValue(record, markerBy);
      const index = markerCategories.indexOf(category);
      if (index === -1) return 'circle';
      return MARKER_SHAPES[index % MARKER_SHAPES.length];
    };

    // Create size mapping
    const getSize = (record: PlotFailureRecord, index: number): number => {
      const baseSize = 8;
      if (sizeBy === 'uniform') return baseSize;
      if (sizeBy === 'operationTime') {
        const time = operationTimes[index];
        const normalized = (time - dataMin) / (dataMax - dataMin);
        return baseSize + normalized * 8; // 8-16px range
      }
      if (sizeBy === 'recent') {
        const date = new Date(record.failure_date);
        const now = new Date();
        const daysAgo = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
        if (daysAgo < 30) return 16;
        if (daysAgo < 90) return 12;
        if (daysAgo < 365) return 10;
        return 6;
      }
      return baseSize;
    };

    // Get unique categories for color and marker mapping - shared between CDF and probability
    const colorCategories = getUniqueCategories(colorBy);
    const markerCategories = getUniqueCategories(markerBy);

    if (chartType === 'cdf') {
      const fittedCDF = analysis.plotData.fittedLine
        .filter((point) => point.x >= dataMin && point.x <= dataMax)
        .map((point) => [point.x, point.y * 100]);

      const series: echarts.SeriesOption[] = [];

      const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

      if (needsCategorization) {
        const createSeriesForCategory = (
          categoryType: string,
          categories: string[],
          isSecondary: boolean = false
        ) => {
          const dataByCategory: Record<string, Array<{
            value: [number, number];
            record: PlotFailureRecord;
            originalTime: number;
            originalProbability: number;
            symbol: string;
            symbolSize: number;
            itemStyle: {
              color: string;
              borderColor: string;
              borderWidth: number;
              opacity?: number;
            };
          }>> = {};

          analysis.plotData.operationTimes.forEach((time, i) => {
            const record = analysis.plotData.failureRecords?.[i];

            let category: string;
            if (categoryType === 'size') {
              const size = getSize(record, i);
              if (size <= 8) category = 'Small';
              else if (size <= 12) category = 'Medium';
              else category = 'Large';
            } else {
              category = getCategoryValue(record, categoryType as ColorByOption | MarkerByOption);
            }

            const limitedCategory = categories.includes(category) ? category : 'Other';

            if (!dataByCategory[limitedCategory]) {
              dataByCategory[limitedCategory] = [];
            }

            let dataPointColor: string;
            if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
              const actualColorCategory = getCategoryValue(record, colorBy);
              const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
              dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
            } else {
              dataPointColor = '#3b82f6';
            }

            const opacity = isSecondary ? 0.6 : 1;

            dataByCategory[limitedCategory].push({
              value: [time, analysis.plotData.probabilities[i] * 100],
              record,
              originalTime: time,
              originalProbability: analysis.plotData.probabilities[i] * 100,
              symbol: getMarker(record),
              symbolSize: getSize(record, i),
              itemStyle: {
                color: dataPointColor,
                borderColor: dataPointColor,
                borderWidth: 1,
                opacity: opacity
              }
            });
          });

          categories.forEach((category, index) => {
            const categoryData = dataByCategory[category] || [];
            if (categoryData.length > 0) {
              const seriesName = category;

              const seriesConfig: echarts.SeriesOption = {
                name: seriesName,
                type: 'scatter',
                data: categoryData,
                emphasis: {
                  scale: 1.5,
                },
              };

              if (isSecondary) {
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1,
                  opacity: 0.6
                };
                seriesConfig.symbol = categoryType === markerBy ?
                  MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
                seriesConfig.symbolSize = 6;
              } else {
                if (categoryType === colorBy) {
                  const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                  seriesConfig.color = color;
                  seriesConfig.itemStyle = {
                    color: color,
                    borderColor: color,
                    borderWidth: 1
                  };
                } else {
                  seriesConfig.color = '#3b82f6';
                  seriesConfig.itemStyle = {
                    color: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: 1
                  };
                }

                if (categoryType === markerBy) {
                  seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
                }
              }

              series.push(seriesConfig);
            }
          });
        };

        if (colorBy !== 'none') {
          createSeriesForCategory(colorBy, colorCategories, false);
        } else if (markerBy !== 'none') {
          createSeriesForCategory(markerBy, markerCategories, false);
        } else if (sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
        }

        if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
          createSeriesForCategory(markerBy, markerCategories, true);
        }

        if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
        }
      } else {
        const cdfData = analysis.plotData.operationTimes.map((time, i) => ({
          value: [time, analysis.plotData.probabilities[i] * 100],
          record: analysis.plotData.failureRecords?.[i],
          originalTime: time,
          originalProbability: analysis.plotData.probabilities[i] * 100,
          symbol: getMarker(analysis.plotData.failureRecords?.[i]),
          symbolSize: getSize(analysis.plotData.failureRecords?.[i], i),
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          }
        }));

        series.push({
          name: 'Empirical CDF',
          type: 'scatter',
          color: '#3b82f6',
          data: cdfData,
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          },
          emphasis: {
            scale: 1.5,
          },
        });
      }

      series.push({
        name: 'Fitted Weibull CDF',
        type: 'line',
        data: fittedCDF,
        smooth: false,
        showSymbol: false,
        lineStyle: {
          color: '#ef4444',
          width: 2,
        },
      });

      if (showConfidenceIntervals && analysis.plotData.confidenceBands) {
        const lowerBounds = analysis.plotData.confidenceBands.lower
          .filter((p) => p.x >= dataMin && p.x <= dataMax)
          .map((p) => [p.x, p.y * 100]);
        const upperBounds = analysis.plotData.confidenceBands.upper
          .filter((p) => p.x >= dataMin && p.x <= dataMax)
          .map((p) => [p.x, p.y * 100]);

        if (lowerBounds.length > 0 && upperBounds.length > 0) {
          const confidenceArea = [
            ...lowerBounds,
            ...upperBounds.reverse()
          ];

          series.push({
            name: 'Confidence Band',
            type: 'line',
            data: confidenceArea,
            showSymbol: false,
            lineStyle: {
              opacity: 0,
            },
            areaStyle: {
              color: 'rgba(59, 130, 246, 0.15)',
            },
            silent: true,
            z: 1, // Below other series
            tooltip: {
              show: false, // Don't show tooltip for confidence band
            },
          });

          // Add optional boundary lines for better visibility
          series.push(
            {
              name: 'Lower CI',
              type: 'line',
              data: lowerBounds,
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
              tooltip: {
                show: false,
              },
            },
            {
              name: 'Upper CI',
              type: 'line',
              data: upperBounds.reverse(), // Reverse back to original order
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
              tooltip: {
                show: false,
              },
            }
          );
        }
      }

      // Calculate dynamic padding based on legend positioning
      const baseBottomPadding = 60;
      const bottomPadding = baseBottomPadding + 30; // Space for primary legend at bottom
      // Calculate right padding based on secondary/tertiary legends on the right
      let rightPadding = 80; // Base padding
      if (showLegend && needsCategorization) {
        // Check if legends will be combined (same categorization for color and marker)
        const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

        // Only add extra padding when secondary/tertiary legends will actually be on the right
        const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
        const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';

        if (hasSecondaryLegend || hasTertiaryLegend) {
          rightPadding = 160; // Extra space for right-positioned legends
        }
      } else if (showLegend) {
        rightPadding = 80; // Basic padding for simple legends
      } else {
        rightPadding = 20; // Minimal padding when no legend
      }

      const option: EChartsOption = {
        grid: {
          left: 60,
          right: rightPadding,
          top: 35,
          bottom: bottomPadding,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: 'Operation Time (years)',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 11,
            color: '#374151',
          },
          min: minBoundary,
          max: maxBoundary,
          axisLabel: {
            formatter: (value: number) => {
              if (value < 1) return value.toFixed(2);
              if (value < 10) return value.toFixed(1);
              return Math.round(value).toString();
            },
            fontSize: 10,
            color: '#6b7280',
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e0e7ff',
              type: 'dashed',
              width: 1,
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'Cumulative Failure Probability (%)',
          nameLocation: 'middle',
          nameGap: 45,
          min: 0,
          max: 100,
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#d1d5db',
          borderWidth: 1,
          borderRadius: 8,
          padding: 0,
          formatter: (params: unknown) => {
            const tooltipParams = params as TooltipParams;
            if (tooltipParams.componentType === 'series' &&
              (tooltipParams.seriesName === 'Empirical CDF' ||
                (needsCategorization && tooltipParams.seriesName !== 'Fitted Weibull CDF' && tooltipParams.seriesName !== 'Confidence Band'))) {
              const record = tooltipParams.data?.record;
              if (!record) return null;

              return `
                <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                  <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                    <p style="font-weight: 600; color: #111827; margin: 0;">Case #${record.case_id}</p>
                    <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${record.turbine_id} • ${record.site_name}</p>
                  </div>
                  <div style="font-size: 14px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Operation Time:</span>
                      <span style="font-weight: 500;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Cumulative Probability:</span>
                      <span style="font-weight: 500; color: #3b82f6;">${tooltipParams.data?.originalProbability?.toFixed(1)}%</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Component:</span>
                      <span style="font-weight: 500;">${record.component_major}</span>
                    </div>
                    ${record.component_minor
                  ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Sub-component:</span>
                      <span style="font-weight: 500;">${record.component_minor}</span>
                    </div>
                    `
                  : ''
                }
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Failure Mode:</span>
                      <span style="font-weight: 500;">${record.failure_mode || 'Unknown'}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Type:</span>
                      <span style="
                        display: inline-flex;
                        padding: 4px 8px;
                        font-size: 12px;
                        font-weight: 600;
                        border-radius: 9999px;
                        ${record.outcome_name.includes('Replace')
                  ? 'background-color: #fee2e2; color: #dc2626;'
                  : record.outcome_name.includes('Planned')
                    ? 'background-color: #dbeafe; color: #1e40af;'
                    : 'background-color: #fef3c7; color: #d97706;'
                }
                      ">
                        ${record.outcome_name}
                      </span>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <span style="color: #6b7280;">Failure Date:</span>
                      <span style="font-weight: 500;">${new Date(record.failure_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              `;
            }

            // Censored data tooltip
            if (tooltipParams.seriesName === 'Censored') {
              const count = tooltipParams.data?.count || 1;
              const turbineText = count === 1 ? 'turbine' : 'turbines';

              return `
                <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                  <div style="font-size: 14px;">
                    <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">Censored Observations</div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Operation Time:</span>
                      <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Number of ${turbineText}:</span>
                      <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                    </div>
                    <div style="font-size: 12px; color: #9ca3af; margin-top: 4px;">Still operational</div>
                  </div>
                </div>
              `;
            }

            return null;
          },
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: false,
              title: {
                zoom: 'Zoom',
                back: 'Reset',
              },
            },
            restore: {
              title: 'Reset',
            },
            saveAsImage: {
              title: 'Save',
              pixelRatio: 2,
            },
          },
          right: rightPadding,
          top: 10,
        },
        series,
      };

      // Add legend if needed
      if (showLegend) {
        if (needsCategorization) {
          // Create multiple legends with flexible positioning
          const legends = [];

          // Check if colorBy and markerBy are the same to combine legends
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Primary Legend: Show main categorization series (positioned below chart)
          if (colorBy !== 'none') {
            // Color-based primary series
            const primaryLegendData = colorCategories.map((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              return {
                name: category, // This matches the series name exactly
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              };
            });

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (markerBy !== 'none') {
            // Marker-based primary series
            const primaryLegendData = markerCategories.map((category, index) => ({
              name: category, // This matches the series name exactly
              icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
              textStyle: { color: '#374151', fontWeight: 'normal' }
            }));

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (sizeBy !== 'uniform') {
            // Size-based primary series
            const sizeData = getSizeLegendData(sizeBy);
            const primaryLegendData = sizeData.map(item => ({
              name: item.name, // This matches the series name exactly
              icon: 'circle',
              textStyle: { color: '#374151', fontWeight: 'normal' }
            }));

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          }

          // Secondary Legend: Show secondary categorization series (positioned on the right)
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const secondaryLegendData = markerCategories.map((category, index) => ({
              name: category, // Remove prefix like "Component:", "Turbine:", etc.
              icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
              itemStyle: {
                color: '#6b7280',
                borderColor: '#6b7280',
                borderWidth: 1
              }, // Single color for all marker legend items
              textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
            }));

            legends.push({
              data: secondaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size categorization series when it's secondary (positioned on the right, below secondary)
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = sizeData.map(item => ({
              name: item.name, // Remove prefix like "Size:"
              icon: 'circle',
              itemStyle: {
                color: '#9ca3af',
                borderColor: '#9ca3af',
                borderWidth: 1
              }, // Single color for all size legend items
              textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
            }));

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          option.legend = legends;
        } else {
          // Basic series legend
          const seriesNames = series
            .filter(s => s.name && !['Lower CI', 'Upper CI', 'Confidence Band'].includes(s.name as string))
            .map(s => s.name as string);

          option.legend = {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: seriesNames,
          };
        }
      }

      return option;
    }

    // Enhanced Probability Plot
    const fittedLine = analysis.plotData.fittedLine
      .filter((point) => point.x >= dataMin && point.x <= dataMax)
      .map((point) => [point.x, weibullY(point.y)]);

    const probTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 50, 70, 80, 90, 95, 99, 99.99];
    const customYValues = probTicks.map((prob) => weibullY(prob / 100));

    const series: echarts.SeriesOption[] = [];

    // Determine if we need to create categorized series (same logic as CDF)
    const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

    if (needsCategorization) {
      // Helper function to create series for a specific categorization (reuse from CDF logic)
      const createSeriesForCategory = (
        categoryType: string,
        categories: string[],
        isSecondary: boolean = false
      ) => {
        const dataByCategory: Record<string, Array<{
          value: [number, number];
          record: PlotFailureRecord;
          originalTime: number;
          originalProbability: number;
          symbol: string;
          symbolSize: number;
          itemStyle: {
            color: string;
            borderColor: string;
            borderWidth: number;
            opacity?: number;
          };
        }>> = {};

        analysis.plotData.operationTimes.forEach((time, i) => {
          const record = analysis.plotData.failureRecords?.[i];

          let category: string;
          if (categoryType === 'size') {
            const size = getSize(record, i);
            if (size <= 8) category = 'Small';
            else if (size <= 12) category = 'Medium';
            else category = 'Large';
          } else {
            category = getCategoryValue(record, categoryType as ColorByOption | MarkerByOption);
          }

          const limitedCategory = categories.includes(category) ? category : 'Other';

          if (!dataByCategory[limitedCategory]) {
            dataByCategory[limitedCategory] = [];
          }

          // Determine color for this data point based on category
          let dataPointColor: string;
          if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
            // Use color categorization when active OR when this is secondary series
            const actualColorCategory = getCategoryValue(record, colorBy);
            const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
            dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
          } else {
            dataPointColor = '#3b82f6';
          }

          const opacity = isSecondary ? 0.6 : 1;

          dataByCategory[limitedCategory].push({
            value: [time, weibullY(analysis.plotData.probabilities[i])],
            record,
            originalTime: time,
            originalProbability: analysis.plotData.probabilities[i] * 100,
            symbol: getMarker(record),
            symbolSize: getSize(record, i),
            itemStyle: {
              color: dataPointColor,
              borderColor: dataPointColor,
              borderWidth: 1,
              opacity: opacity
            }
          });
        });

        // Create series for each category
        categories.forEach((category, index) => {
          const categoryData = dataByCategory[category] || [];
          if (categoryData.length > 0) {
            const seriesName = category; // Use clean names without prefixes for both primary and secondary

            const seriesConfig: echarts.SeriesOption = {
              name: seriesName,
              type: 'scatter',
              data: categoryData,
              emphasis: {
                scale: 1.5,
              },
            };

            // Apply styling based on whether this is primary or secondary
            if (isSecondary) {
              // Secondary series styling - more subtle
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              seriesConfig.color = color; // Series-level color for ECharts fill
              seriesConfig.itemStyle = {
                color: color,
                borderColor: color,
                borderWidth: 1,
                opacity: 0.6
              };
              seriesConfig.symbol = categoryType === markerBy ?
                MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
              seriesConfig.symbolSize = 6; // Smaller for secondary
            } else {
              // Primary series styling - full visibility
              if (categoryType === colorBy) {
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                };
              } else {
                seriesConfig.color = '#3b82f6'; // Series-level color for ECharts fill
                seriesConfig.itemStyle = {
                  color: '#3b82f6',
                  borderColor: '#3b82f6',
                  borderWidth: 1
                };
              }

              if (categoryType === markerBy) {
                seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
              }
            }

            series.push(seriesConfig);
          }
        });
      };

      // Create primary series (first active categorization)
      if (colorBy !== 'none') {
        createSeriesForCategory(colorBy, colorCategories, false);
      } else if (markerBy !== 'none') {
        createSeriesForCategory(markerBy, markerCategories, false);
      } else if (sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
      }

      // Create secondary series (additional categorizations)
      if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
        createSeriesForCategory(markerBy, markerCategories, true);
      }

      if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
      }
    } else {
      // Single series with default styling
      const failureData = analysis.plotData.operationTimes.map((time, i) => ({
        value: [time, weibullY(analysis.plotData.probabilities[i])],
        record: analysis.plotData.failureRecords?.[i],
        originalTime: time,
        originalProbability: analysis.plotData.probabilities[i] * 100,
        symbol: getMarker(analysis.plotData.failureRecords?.[i]),
        symbolSize: getSize(analysis.plotData.failureRecords?.[i], i),
        itemStyle: {
          color: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: 1
        }
      }));

      series.push({
        name: 'Failure Data',
        type: 'scatter',
        color: '#3b82f6', // Series-level color for ECharts fill
        data: failureData,
        itemStyle: {
          color: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: 1
        },
        emphasis: {
          scale: 1.5,
        },
      });
    }

    // Add fitted line
    series.push({
      name: 'Weibull Fit',
      type: 'line',
      data: fittedLine,
      smooth: false,
      showSymbol: false,
      lineStyle: {
        color: '#ef4444',
        width: 2,
      },
      silent: true,
    });

    // Add shaded confidence intervals using a simpler, more reliable approach
    if (showConfidenceIntervals && analysis.plotData.confidenceBands) {
      const lowerBounds = analysis.plotData.confidenceBands.lower
        .filter((p) => p.x >= dataMin && p.x <= dataMax)
        .map((p) => [p.x, weibullY(p.y)]);
      const upperBounds = analysis.plotData.confidenceBands.upper
        .filter((p) => p.x >= dataMin && p.x <= dataMax)
        .map((p) => [p.x, weibullY(p.y)]);

      if (lowerBounds.length > 0 && upperBounds.length > 0) {
        // Create confidence band as an area series
        // Combine lower and upper bounds to create a closed polygon
        const confidenceArea = [
          ...lowerBounds,
          ...upperBounds.reverse() // Reverse to close the polygon
        ];

        series.push({
          name: 'Confidence Band',
          type: 'line',
          data: confidenceArea,
          showSymbol: false,
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.15)',
          },
          silent: true,
          z: 1, // Below other series
          tooltip: {
            show: false, // Don't show tooltip for confidence band
          },
        });

        // Add optional boundary lines for better visibility
        series.push(
          {
            name: 'Lower CI',
            type: 'line',
            data: lowerBounds,
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          },
          {
            name: 'Upper CI',
            type: 'line',
            data: upperBounds.reverse(), // Reverse back to original order
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          }
        );
      }
    }

    // Add censored data if available
    if (showCensoredData && censoredData && censoredData.length > 0) {
      const censoredByTime: Record<number, number> = {};
      censoredData.forEach((point) => {
        const time = point.time;
        if (!censoredByTime[time]) censoredByTime[time] = 0;
        censoredByTime[time]++;
      });

      const censoredY = weibullY(0.0015);
      const censoredPoints = Object.entries(censoredByTime).map(([timeStr, count]) => ({
        value: [parseFloat(timeStr), censoredY],
        originalTime: parseFloat(timeStr),
        count: count,
        symbolSize: count === 1 ? 4 : count <= 10 ? 6 : count <= 50 ? 8 : 10,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8
        }
      }));

      series.push({
        name: 'Censored',
        type: 'scatter',
        color: '#e5e2d8', // Series-level color for ECharts fill
        data: censoredPoints,
        symbolSize: (data: { symbolSize: number }) => data.symbolSize,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8,
        },
      });
    }

    // Calculate right padding based on secondary/tertiary legends on the right
    let rightPadding = 80; // Base padding
    if (showLegend && needsCategorization) {
      // Check if legends will be combined (same categorization for color and marker)
      const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

      // Only add extra padding when secondary/tertiary legends will actually be on the right
      const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
      const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';

      if (hasSecondaryLegend || hasTertiaryLegend) {
        rightPadding = 160; // Extra space for right-positioned legends
      }
    } else if (showLegend) {
      rightPadding = 80; // Basic padding for simple legends
    } else {
      rightPadding = 20; // Minimal padding when no legend
    }

    return {
      grid: {
        left: 60,
        right: rightPadding,
        top: 35,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'log',
        name: 'Operation Time (years)',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: {
          fontSize: 11,
          color: '#374151',
        },
        min: logMinBoundary,
        max: maxBoundary,
        axisLine: {
          onZero: false,
          lineStyle: {
            color: '#374151',
          },
        },
        position: 'bottom',
        offset: 0,
        logBase: 10,
        // Force logarithmic scale behavior with explicit tick configuration
        interval: 0, // Force to show all major ticks
        splitNumber: 8, // Reasonable number of major divisions
        axisLabel: {
          formatter: (value: number) => {
            // Enhanced formatter for logarithmic scale with better precision
            if (value < 1) return value.toFixed(1);
            if (value < 10) return value.toFixed(1);
            if (value < 100) return Math.round(value).toString();
            if (value < 1000) return Math.round(value).toString();
            return (value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toFixed(0));
          },
          fontSize: 10,
          color: '#6b7280',
          rotate: 0, // Keep labels horizontal
          margin: 8,
        },
        axisTick: {
          show: true,
          inside: false,
          interval: 0, // Show all major ticks
          length: 6,
        },
        minorTick: {
          show: true,
          splitNumber: 9, // 9 minor ticks between major ticks for proper log scale
          length: 3,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e0e7ff',
            type: 'dashed',
            width: 1,
          },
        },
        minorSplitLine: {
          show: true,
          lineStyle: {
            color: '#f3f4f6',
            type: 'solid',
            width: 0.5,
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Failure Probability (%)',
        nameLocation: 'middle',
        nameGap: 35,
        min: weibullY(0.001),
        max: weibullY(0.9999),
        axisLabel: {
          customValues: customYValues,
          formatter: (value: number) => {
            const prob = inverseWeibullY(value) * 100;
            let closest = probTicks[0];
            let minDiff = Math.abs(prob - probTicks[0]);
            for (const tick of probTicks) {
              const diff = Math.abs(prob - tick);
              if (diff < minDiff) {
                minDiff = diff;
                closest = tick;
              }
            }
            return `${closest}%`;
          },
        },
      },
      legend: showLegend ? (() => {
        if (needsCategorization) {
          // Check if colorBy and markerBy are the same to combine legends
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Create multiple legends for different customizations
          const legends = [];

          // Primary Legend: Always show the main categorization (color > marker > size priority)
          if (colorBy !== 'none') {
            // Color-based series - primary legend
            const colorLegendData = [];
            colorCategories.forEach((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              colorLegendData.push({
                name: category,
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: colorLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (markerBy !== 'none') {
            // Marker-based series - primary legend
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category,
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (sizeBy !== 'uniform') {
            // Size-based series - primary legend
            const sizeData = getSizeLegendData(sizeBy);
            const sizeLegendData = [];
            sizeData.forEach(item => {
              sizeLegendData.push({
                name: item.name,
                icon: 'circle',
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: sizeLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          }

          // Secondary Legend: Show marker information when color is primary and not combined
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category, // Remove prefix like "Component:", "Turbine:", etc.
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                itemStyle: {
                  color: '#6b7280',
                  borderColor: '#6b7280',
                  borderWidth: 1
                }, // Single color for all marker legend items
                textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size information when not primary
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = [];
            sizeData.forEach(item => {
              tertiaryLegendData.push({
                name: item.name, // Remove prefix like "Size:"
                icon: 'circle',
                itemStyle: {
                  color: '#9ca3af',
                  borderColor: '#9ca3af',
                  borderWidth: 1
                }, // Single color for all size legend items
                textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          return legends;
        } else {
          // Basic series legend
          return {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: series
              .filter(s => s.name && !['Lower CI', 'Upper CI', 'Confidence Band'].includes(s.name as string))
              .map(s => s.name as string),
          };
        }
      })() : undefined,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#d1d5db',
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        formatter: (params: unknown) => {
          const tooltipParams = params as TooltipParams;
          if (tooltipParams.componentType === 'series' &&
            (tooltipParams.seriesName === 'Failure Data' ||
              (needsCategorization && tooltipParams.seriesName !== 'Weibull Fit' && tooltipParams.seriesName !== 'Confidence Band' && tooltipParams.seriesName !== 'Censored'))) {
            const record = tooltipParams.data?.record;
            if (!record) return null;

            return `
              <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                  <p style="font-weight: 600; color: #111827; margin: 0;">Case #${record.case_id}</p>
                  <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${record.turbine_id} • ${record.site_name}</p>
                </div>
                <div style="font-size: 14px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Probability:</span>
                    <span style="font-weight: 500; color: #3b82f6;">${tooltipParams.data?.originalProbability?.toFixed(1)}%</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Component:</span>
                    <span style="font-weight: 500;">${record.component_major}</span>
                  </div>
                  ${record.component_minor
                ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Sub-component:</span>
                    <span style="font-weight: 500;">${record.component_minor}</span>
                  </div>
                  `
                : ''
              }
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Mode:</span>
                    <span style="font-weight: 500;">${record.failure_mode || 'Unknown'}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Type:</span>
                    <span style="
                      display: inline-flex;
                      padding: 4px 8px;
                      font-size: 12px;
                      font-weight: 600;
                      border-radius: 9999px;
                      ${record.outcome_name.includes('Replace')
                ? 'background-color: #fee2e2; color: #dc2626;'
                : record.outcome_name.includes('Planned')
                  ? 'background-color: #dbeafe; color: #1e40af;'
                  : 'background-color: #fef3c7; color: #d97706;'
              }
                    ">
                      ${record.outcome_name}
                    </span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span style="color: #6b7280;">Failure Date:</span>
                    <span style="font-weight: 500;">${new Date(record.failure_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            `;
          }

          // Censored data tooltip
          if (tooltipParams.seriesName === 'Censored') {
            const count = tooltipParams.data?.count || 1;
            const turbineText = count === 1 ? 'turbine' : 'turbines';

            return `
              <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                <div style="font-size: 14px;">
                  <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">Censored Observations</div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Number of ${turbineText}:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                  </div>
                  <div style="font-size: 12px; color: #9ca3af; margin-top: 4px;">Still operational</div>
                </div>
              </div>
            `;
          }

          return null;
        },
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: false,
            title: {
              zoom: 'Zoom',
              back: 'Reset',
            },
          },
          restore: {
            title: 'Reset',
          },
          saveAsImage: {
            title: 'Save',
            pixelRatio: 2,
          },
        },
        right: rightPadding,
        top: 10,
      },
      series,
    };
  }, [analysis, showConfidenceIntervals, showCensoredData, censoredData, chartType, colorBy, markerBy, sizeBy, showLegend]);
};
